import { collection, doc, getDoc, getDocs, updateDoc, deleteDoc, query, where, addDoc, Firestore, writeBatch } from 'firebase/firestore';
import { Branch, Warehouse, User } from '../utils/types';
import { getAllWarehouses, updateWarehouse, createWarehouse, getWarehouseById } from './inventoryService';
import { getUserById, updateUser } from './userService';
import { db } from '../config/firebase';

const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore غير متوفر. تأكد من تهيئة Firebase بشكل صحيح.');
  }
  return db;
};

const getBranchesCollection = () => collection(ensureFirestore(), 'branches');

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

export const createBranch = async (branch: Branch): Promise<string> => {
  try {
    const branchData: Branch = {
      ...branch,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      warehouseIds: branch.warehouseIds || []
    };

    const branchRef = await addDoc(getBranchesCollection(), branchData);
    const branchId = branchRef.id;

    if (!branch.warehouseIds || branch.warehouseIds.length === 0) {
      const warehouseId = await createWarehouse(`مخزن ${branch.name} الرئيسي`, branch.address);
      await updateWarehouse(warehouseId, { branchId, branchName: branch.name, isMain: true });
      await updateBranch(branchId, { warehouseIds: [warehouseId], defaultWarehouseId: warehouseId });
    } else {
      for (const warehouseId of branch.warehouseIds) {
        await updateWarehouse(warehouseId, { branchId, branchName: branch.name });
      }
    }

    return branchId;
  } catch (error) {
    console.error('Error creating branch:', error);
    throw error;
  }
};

export const getAllBranches = async (includeInactive: boolean = false): Promise<Branch[]> => {
  try {
    const branchesCollection = getBranchesCollection();
    let q = query(branchesCollection);
    if (!includeInactive) q = query(q, where('isActive', '==', true));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Branch));
  } catch (error) {
    console.error('Error getting branches:', error);
    throw error;
  }
};

export const getBranchById = async (id: string): Promise<Branch | null> => {
  try {
    const branchDoc = await getDoc(doc(ensureFirestore(), 'branches', id));
    if (!branchDoc.exists()) return null;
    return { ...branchDoc.data(), id: branchDoc.id } as Branch;
  } catch (error) {
    console.error('Error getting branch:', error);
    throw error;
  }
};

export const updateBranch = async (id: string, updates: Partial<Branch>): Promise<void> => {
  try {
    const branchRef = doc(ensureFirestore(), 'branches', id);
    const branchDoc = await getDoc(branchRef);
    if (!branchDoc.exists()) throw new Error(`الفرع غير موجود: ${id}`);

    const currentBranch = branchDoc.data() as Branch;
    if (updates.name && updates.name !== currentBranch.name) {
      const warehouseIds = updates.warehouseIds || currentBranch.warehouseIds || [];
      for (const warehouseId of warehouseIds) {
        await updateWarehouse(warehouseId, { branchName: updates.name });
      }
    }

    if (updates.warehouseIds && JSON.stringify(updates.warehouseIds) !== JSON.stringify(currentBranch.warehouseIds)) {
      for (const warehouseId of updates.warehouseIds) {
        if (!currentBranch.warehouseIds || !currentBranch.warehouseIds.includes(warehouseId)) {
          await updateWarehouse(warehouseId, { branchId: id, branchName: updates.name || currentBranch.name });
        }
      }
      for (const warehouseId of currentBranch.warehouseIds || []) {
        if (!updates.warehouseIds.includes(warehouseId)) {
          await updateWarehouse(warehouseId, { branchId: '', branchName: '' });
        }
      }
    }

    await updateDoc(branchRef, { ...updates, updatedAt: new Date() });
  } catch (error) {
    console.error('Error updating branch:', error);
    throw error;
  }
};

export const deleteBranch = async (id: string, force: boolean = false): Promise<void> => {
  try {
    const branchRef = doc(ensureFirestore(), 'branches', id);
    const branchDoc = await getDoc(branchRef);
    if (!branchDoc.exists()) throw new Error(`الفرع غير موجود: ${id}`);

    const branchData = branchDoc.data() as Branch;
    if (!force && branchData.warehouseIds && branchData.warehouseIds.length > 0) {
      throw new Error('لا يمكن حذف الفرع لأنه يحتوي على مخازن مرتبطة به.');
    }

    const usersQuery = query(collection(ensureFirestore(), 'users'), where('branchId', '==', id));
    const usersSnapshot = await getDocs(usersQuery);
    if (!force && !usersSnapshot.empty) {
      throw new Error('لا يمكن حذف الفرع لأنه يحتوي على موظفين مرتبطين به.');
    }

    if (force && branchData.warehouseIds) {
      for (const warehouseId of branchData.warehouseIds) {
        await updateWarehouse(warehouseId, { branchId: '', branchName: '' });
      }
    }
    if (force && !usersSnapshot.empty) {
      for (const userDoc of usersSnapshot.docs) {
        await updateUser(userDoc.id, { branchId: '', branchName: '' });
      }
    }

    await deleteDoc(branchRef);
  } catch (error) {
    console.error('Error deleting branch:', error);
    throw error;
  }
};

export const setBranchActive = async (id: string, active: boolean): Promise<void> => {
  try {
    const branchRef = doc(ensureFirestore(), 'branches', id);
    await updateDoc(branchRef, { isActive: active, updatedAt: new Date() });
  } catch (error) {
    console.error('Error setting branch active status:', error);
    throw error;
  }
};

export const getBranchWarehouses = async (branchId: string): Promise<Warehouse[]> => {
  try {
    const branch = await getBranchById(branchId);
    if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);
    const warehouses = await getAllWarehouses();
    return warehouses.filter(w => branch.warehouseIds?.includes(w.id));
  } catch (error) {
    console.error('Error getting branch warehouses:', error);
    throw error;
  }
};

export const addWarehouseToBranch = async (branchId: string, warehouseId: string, isDefault: boolean = false): Promise<void> => {
  try {
    const branch = await getBranchById(branchId);
    if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);

    const warehouseIds = branch.warehouseIds || [];
    if (warehouseIds.includes(warehouseId)) {
      if (isDefault) await updateBranch(branchId, { defaultWarehouseId: warehouseId });
      return;
    }

    await updateBranch(branchId, {
      warehouseIds: [...warehouseIds, warehouseId],
      defaultWarehouseId: isDefault ? warehouseId : branch.defaultWarehouseId
    });
    await updateWarehouse(warehouseId, { branchId, branchName: branch.name });
  } catch (error) {
    console.error('Error adding warehouse to branch:', error);
    throw error;
  }
};

export const removeWarehouseFromBranch = async (branchId: string, warehouseId: string): Promise<void> => {
  try {
    const branch = await getBranchById(branchId);
    if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);

    const warehouseIds = branch.warehouseIds || [];
    if (!warehouseIds.includes(warehouseId)) return;
    if (warehouseIds.length === 1) throw new Error('لا يمكن إزالة المخزن الوحيد من الفرع.');

    const newWarehouseIds = warehouseIds.filter(id => id !== warehouseId);
    let defaultWarehouseId = branch.defaultWarehouseId;
    if (branch.defaultWarehouseId === warehouseId) defaultWarehouseId = newWarehouseIds[0];

    await updateBranch(branchId, { warehouseIds: newWarehouseIds, defaultWarehouseId });
    await updateWarehouse(warehouseId, { branchId: '', branchName: '' });
  } catch (error) {
    console.error('Error removing warehouse from branch:', error);
    throw error;
  }
};

export const setDefaultWarehouse = async (branchId: string, warehouseId: string): Promise<void> => {
  try {
    const branch = await getBranchById(branchId);
    if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);
    const warehouseIds = branch.warehouseIds || [];
    if (!warehouseIds.includes(warehouseId)) throw new Error('المخزن غير مرتبط بهذا الفرع.');
    await updateBranch(branchId, { defaultWarehouseId: warehouseId });
  } catch (error) {
    console.error('Error setting default warehouse:', error);
    throw error;
  }
};

export const assignEmployeeToBranch = async (userId: string, branchId: string): Promise<void> => {
  try {
    const user = await getUserById(userId);
    const branch = await getBranchById(branchId);
    if (!user) throw new Error(`المستخدم غير موجود: ${userId}`);
    if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);

    await updateUser(userId, { branchId, branchName: branch.name });
    const allowedEmployees = branch.allowedEmployees || [];
    if (!allowedEmployees.includes(userId)) {
      await updateBranch(branchId, { allowedEmployees: [...allowedEmployees, userId] });
    }
  } catch (error) {
    console.error('Error assigning employee to branch:', error);
    throw error;
  }
};

export const getBranchEmployees = async (branchId: string): Promise<User[]> => {
  try {
    const q = query(collection(ensureFirestore(), 'users'), where('branchId', '==', branchId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as User));
  } catch (error) {
    console.error('Error getting branch employees:', error);
    throw error;
  }
};

export const isWarehouseLinkedToBranch = async (warehouseId: string): Promise<boolean> => {
  try {
    const q = query(collection(ensureFirestore(), 'branches'), where('warehouseIds', 'array-contains', warehouseId));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking warehouse branch relation:', error);
    throw error;
  }
};

export default {
  createBranch, getAllBranches, getBranchById, updateBranch, deleteBranch,
  setBranchActive, getBranchWarehouses, addWarehouseToBranch, removeWarehouseFromBranch,
  setDefaultWarehouse, assignEmployeeToBranch, getBranchEmployees, isWarehouseLinkedToBranch
};
